// Domain-owned route containers. Do not import legacy connected-container or all-domain registries here.
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type {
  EnterpriseNotification,
  EnterpriseTask,
  EvidenceItem,
  WorkflowCommand,
} from "@swot/swot-components";
import { readCmtsSession } from "../../auth/authStore.js";
import { resolveCurrentCmtsSession } from "../../session/cmtsSessionResolver.js";
import { useCmtsDispatch } from "@store/hooks.js";
import { authActions } from "@store/authSlice.js";
import { permissionActions } from "@store/permissionSlice.js";
import { tenantActions } from "@store/tenantSlice.js";
import { serializeReduxError } from "@store/thunkExtra.js";
import { useCmtsAuth } from "../../auth/authHooks.js";
import { useComplaintRegister } from "@hooks/useComplaintRegister.js";
import { useComplaintDetail } from "@hooks/useComplaintDetail.js";
import { useComplaintWorkflow } from "@hooks/useComplaintWorkflow.js";
import { useEvidence } from "@hooks/useEvidence.js";
import { cmtsKeys } from "@hooks/cmtsQueryKeys.js";
import type { RepositoryResult } from "../../repositories/repositoryResult.js";
import type { CmtsHostRuntime } from "../../../../app/appRuntime.js";
import type {
  CmtsComplaintDetailVm,
  CmtsComplaintRecordVm,
} from "../../view-models/complaintViewModels.js";
import type {
  CmtsConfigurationDraftVm,
  CmtsConfigurationVersionVm,
} from "../../mappers/configurationMappers.js";
import { queryObjectFromSearch } from "../../filters/cmtsFilterEngine.js";
import {
  decideCmtsExport,
  createCmtsExportAuditPayload,
} from "../../export/cmtsSecureExport.js";
import { getCmtsPersonaLandingPath } from "../../routes/cmtsPersonaLanding.js";
import {
  normalizeTypedReportCode,
  reportCodeFromPathname,
} from "../../reporting/cmtsReportConfig.js";
import { throwCmtsCanonicalError } from "../../api/cmtsApiErrors.js";
import { normalizeUserSafeApiError } from "../../api/apiErrorNormalizer.js";
import { isCmtsGuid, requireCmtsGuid } from "../../utils/cmtsGuid.js";
import { cmtsMasterDataEntityForPathSegment } from "../../master-data/cmtsEnterpriseMasterDataRegistry.js";
import {
  activeRows,
  assertRepositoryOk,
  businessValueOptionOf,
  complaintQuery,
  dataOr,
  diagnosticsOf,
  emptyBackendDashboard,
  errorOf,
  invalidateAdminArea,
  loadMasterOptions,
  optionOf,
  tenantId,
  useAdminDependencyData,
  useCmtsRouterSearch,
  type CmtsConnectedContainerProps,
} from "../domainContainerSupport.js";
import { CmtsMyWorkspacePage } from "@modules/pages/cmtsMyWorkspacePage.js";
import { CmtsTasksPage } from "@modules/pages/cmtsTasksPage.js";
import { CmtsNotificationsPage } from "@modules/pages/cmtsNotificationsPage.js";
import { CmtsMessagesPage } from "@modules/pages/cmtsMessagesPage.js";
import { CMTS_MESSAGE_VISIBILITY_OPTIONS } from "../../master-data/cmtsCanonicalFormOptions.js";

function useComplaintId(): string | undefined {
  return useParams<{ complaintId: string }>().complaintId;
}

type WorkspaceRow = Record<string, unknown>;

function workspaceId(
  row: WorkspaceRow,
  keys: readonly string[],
  operation: string,
): string {
  for (const key of keys) {
    const value = row[key];
    if (isCmtsGuid(value)) return value.trim();
  }
  throwCmtsCanonicalError({
    code: "CMTS_ERR_INVALID_ROUTE_PARAM",
    message: `${operation} requires a persisted backend GUID id.`,
    details: { operation, keys },
  });
}

function optionalLinkedComplaintPath(
  row: WorkspaceRow,
  suffix: string,
): string | null {
  const value = row.complaintId ?? row.linkedEntityId;
  return isCmtsGuid(value)
    ? `/cmts/complaints/${encodeURIComponent(value)}${suffix}`
    : null;
}

function firstUserSafeError(errors: readonly unknown[]): string | null {
  const error = errors.find(Boolean);
  return error ? normalizeUserSafeApiError(error).userMessage : null;
}

export function CmtsMessagesContainer({
  runtime,
}: CmtsConnectedContainerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const t = tenantId(runtime);
  const complaintIdFromRoute =
    useComplaintId();

  const session =
    readCmtsSession();

  const actorId =
    session?.userId ??
    "anonymous";

  /*
   * User-specific query key.
   *
   * Different users login/logout செய்யும்போது
   * previous user's cached messages show ஆகாது.
   */
  const messageQueryKey =
    complaintIdFromRoute
      ? [
          "cmts",
          "messages",
          complaintIdFromRoute,
          t,
          actorId,
        ]
      : [
          "cmts",
          "messages",
          t,
          actorId,
        ];

  const taskQueryKey = [
    ...cmtsKeys.tasks(t),
    actorId,
  ];

  const notificationQueryKey = [
    ...cmtsKeys.notifications(t),
    actorId,
  ];

  /*
   * Message list.
   *
   * Backend:
   * - Platform Admin -> all tenant messages
   * - Other users -> sent/received messages only
   */
  const query = useQuery({
    queryKey:
      messageQueryKey,

    queryFn: () =>
      complaintIdFromRoute
        ? runtime.repositories.messages
            .forComplaint(
              complaintIdFromRoute,
              t,
            )
        : runtime.repositories.messages
            .threads(t),

    enabled: Boolean(
      session?.userId &&
      t,
    ),

    staleTime: 30_000,

    retry: 1,
  });

  /*
   * Visibility dropdown.
   */
  const visibilityOptionsQuery =
    useQuery({
      queryKey: [
        "cmts",
        "master-options",
        "message-visibility",
        t,
      ],

      queryFn: () =>
        loadMasterOptions(
          runtime,
          "message-visibility",
          t,
          "message-visibility",
        ),

      enabled: Boolean(t),

      retry: 1,
    });

  const visibilityOptionsResolved =
    visibilityOptionsQuery.data &&
    visibilityOptionsQuery.data
      .length > 0
      ? visibilityOptionsQuery.data
      : CMTS_MESSAGE_VISIBILITY_OPTIONS;

  /*
   * Recipient / assignee users.
   */
const usersQuery =
  useQuery({
    queryKey: [
      "cmts",
      "message-lifecycle-users",
      t,
    ],

    queryFn: () =>
      runtime.repositories.reference
        .lifecycleUsers(t),

    enabled: Boolean(
      session?.userId &&
      t,
    ),

    retry: 1,
  });

  /*
   * Supports:
   *
   * []
   * { items: [] }
   * { records: [] }
   * { data: [] }
   */
  const usersRaw =
    dataOr<unknown>(
      usersQuery.data,
      [],
    );

  const usersArr:
    readonly Record<
      string,
      unknown
    >[] =
    Array.isArray(usersRaw)
      ? usersRaw.filter(
          (
            row,
          ): row is Record<
            string,
            unknown
          > =>
            typeof row ===
              "object" &&
            row !== null &&
            !Array.isArray(row),
        )
      : usersRaw &&
          typeof usersRaw ===
            "object" &&
          Array.isArray(
            (
              usersRaw as Record<
                string,
                unknown
              >
            ).items,
          )
        ? (
            usersRaw as {
              items: readonly Record<
                string,
                unknown
              >[];
            }
          ).items
        : usersRaw &&
            typeof usersRaw ===
              "object" &&
            Array.isArray(
              (
                usersRaw as Record<
                  string,
                  unknown
                >
              ).records,
            )
          ? (
              usersRaw as {
                records: readonly Record<
                  string,
                  unknown
                >[];
              }
            ).records
          : [];

  const userOptionsResolved =
    useMemo(
      () =>
        usersArr
          .map((row) => {
            const value = String(
              row.value ??
              row.userId ??
              row.id ??
              "",
            ).trim();

            const code = String(
              row.code ??
              row.userCode ??
              "",
            ).trim();

            const metadataEmail =
              row.metadata && typeof row.metadata === "object"
                ? String((row.metadata as Record<string, unknown>).email ?? "").trim()
                : "";

            const rawLabel = String(
              row.label ??
              row.fullName ??
              row.displayName ??
              row.userName ??
              row.name ??
              row.loginId ??
              row.email ??
              metadataEmail ??
              "",
            ).trim();

            const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(rawLabel);
            const baseName = !isGuid ? rawLabel : (code || rawLabel);

            const label =
              baseName && code && code.toLowerCase() !== baseName.toLowerCase()
                ? `${baseName} (${code})`
                : baseName || code || value;

            return {
              value,
              label,
            };
          })
          .filter(
            (option) =>
              Boolean(
                option.value,
              ) &&
              Boolean(
                option.label,
              ),
          ),
      [usersArr],
    );

  /*
   * Linked complaint dropdown.
   */
  const complaintsQuery =
    useQuery({
      queryKey: [
        "cmts",
        "message-complaints",
        t,
      ],

      queryFn: () =>
        runtime.repositories.complaints
          .list({
            tenantId: t,
            pageNumber: 1,
            pageSize: 200,
          }),

      enabled: Boolean(t),

      retry: 1,
    });

  const complaintRows =
    dataOr<
      readonly Record<
        string,
        unknown
      >[]
    >(
      complaintsQuery.data,
      [],
    );

  const complaintOptionsResolved =
    useMemo(
      () =>
        complaintRows
          .map((row) => ({
            value: String(
              row.id ??
              row.complaintId ??
              "",
            ).trim(),

            label: String(
              row.complaintNumber ??
              row.complaintCode ??
              row.code ??
              row.title ??
              "Complaint",
            ).trim(),
          }))
          .filter(
            (option) =>
              Boolean(
                option.value,
              ),
          ),
      [complaintRows],
    );

  /*
   * Message-related mutations முடிந்ததும்
   * messages, tasks and notifications refresh ஆகும்.
   */
  const invalidateMessages =
    async (): Promise<void> => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey:
            messageQueryKey,

          refetchType:
            "active",
        }),

        queryClient.invalidateQueries({
          queryKey:
            taskQueryKey,

          refetchType:
            "active",
        }),

        queryClient.invalidateQueries({
          queryKey:
            notificationQueryKey,

          refetchType:
            "active",
        }),
      ]);

      await queryClient.refetchQueries({
        queryKey:
          messageQueryKey,

        type:
          "active",
      });
    };

  /*
   * Create a new message thread.
   *
   * Route complaintId இருந்தால்,
   * form complaintId empty ஆக இருக்கும்போது
   * route complaint automatically பயன்படுத்தப்படும்.
   */
  const create =
    useMutation({
      mutationFn: (
        payload:
          Record<
            string,
            unknown
          >,
      ) => {
        const selectedComplaintId =
          typeof payload
            .complaintId ===
              "string" &&
          payload.complaintId
            .trim()
            ? payload.complaintId
                .trim()
            : complaintIdFromRoute;

        return runtime.repositories
          .messages
          .createThread({
            ...payload,

            tenantId:
              t,

            complaintId:
              selectedComplaintId,

            messageBody:
              payload.messageBody ??
              payload.body,

            visibilityCode:
              payload.visibilityCode ??
              payload.visibility,
          });
      },

      onSuccess:
        invalidateMessages,
    });

  /*
   * Reply to existing thread.
   */
  const reply =
    useMutation({
      mutationFn: ({
        row,
        payload,
      }: {
        row:
          WorkspaceRow;

        payload:
          Record<
            string,
            unknown
          >;
      }) =>
        runtime.repositories.messages
          .addToThread(
            workspaceId(
              row,
              [
                "threadId",
                "messageThreadId",
                "id",
              ],
              "Message reply",
            ),

            {
              ...payload,

              messageBody:
                payload.messageBody ??
                payload.body,

              messageTypeCode:
                payload.messageTypeCode ??
                "REPLY",

              complaintId:
                payload.complaintId ??
                row.complaintId,

              tenantId:
                t,
            },

            t,
          ),

      onSuccess:
        invalidateMessages,
    });

  /*
   * Archive is current-user specific.
   */
  const archive =
    useMutation({
      mutationFn: (
        row:
          WorkspaceRow,
      ) =>
        runtime.repositories.messages
          .archive(
            workspaceId(
              row,
              [
                "threadId",
                "messageThreadId",
                "id",
              ],
              "Message archive",
            ),

            {
              archivedBy:
                session?.userId,
            },

            t,
          ),

      onSuccess:
        invalidateMessages,
    });

  const markRead =
    useMutation({
      mutationFn: (
        row:
          WorkspaceRow,
      ) =>
        runtime.repositories.messages
          .markRead(
            workspaceId(
              row,
              [
                "threadId",
                "messageThreadId",
                "id",
              ],
              "Message mark read",
            ),

            t,
          ),

      onSuccess:
        invalidateMessages,
    });

  const markUnread =
    useMutation({
      mutationFn: (
        row:
          WorkspaceRow,
      ) =>
        runtime.repositories.messages
          .markUnread(
            workspaceId(
              row,
              [
                "threadId",
                "messageThreadId",
                "id",
              ],
              "Message mark unread",
            ),

            t,
          ),

      onSuccess:
        invalidateMessages,
    });

  /*
   * Important:
   *
   * Message boundary createTask method
   * governed event மட்டும் record செய்யலாம்.
   *
   * Actual task create ஆக tasks.create
   * repository பயன்படுத்த வேண்டும்.
   */
  const createTask =
    useMutation({
      mutationFn: ({
        row,
        payload,
      }: {
        row:
          WorkspaceRow;

        payload:
          Record<
            string,
            unknown
          >;
      }) => {
        const complaintId =
          String(
            payload.complaintId ??
            payload.linkedEntityId ??
            row.complaintId ??
            row.linkedEntityId ??
            "",
          ).trim();

        if (!complaintId) {
          throw new Error(
            "A linked complaint is required to create a task.",
          );
        }

        const assignedToUserId =
          String(
            payload.assignedToUserId ??
            payload.assigneeId ??
            "",
          ).trim();

        if (
          !assignedToUserId
        ) {
          throw new Error(
            "Please select a task assignee.",
          );
        }

        return runtime.repositories.tasks
          .create(
            {
              ...payload,

              complaintId,

              parentComplaintId:
                complaintId,

              linkedEntityId:
                complaintId,

              linkedEntityType:
                "Complaint",

              assignedToUserId,

              ownerUserId:
                payload.ownerUserId ??
                assignedToUserId,

              taskTypeCode:
                payload.taskTypeCode ??
                "MESSAGE_FOLLOW_UP",

              priorityCode:
                payload.priorityCode ??
                "MEDIUM",

              title:
                payload.title ??
                "Message follow-up",

              description:
                payload.description ??
                payload.remarks ??
                "",
            },

            t,
          );
      },

      onSuccess:
        invalidateMessages,
    });

  const pending =
    create.isPending ||
    reply.isPending ||
    archive.isPending ||
    markRead.isPending ||
    markUnread.isPending ||
    createTask.isPending;

  const error =
    firstUserSafeError([
      create.error,
      reply.error,
      archive.error,
      markRead.error,
      markUnread.error,
      createTask.error,
    ]);

  /*
   * User Id -> display name lookup.
   */
  const userNameMap =
    useMemo(() => {
      const map =
        new Map<
          string,
          string
        >();

      for (
        const option of
        userOptionsResolved
      ) {
        if (
          option.value
        ) {
          map.set(
            option.value
              .toLowerCase(),

            option.label,
          );
        }
      }

      if (
        session?.userId &&
        !map.has(
          session.userId
            .toLowerCase(),
        )
      ) {
        map.set(
          session.userId
            .toLowerCase(),

          session.displayName ??
          session.email ??
          session.userId,
        );
      }

      return map;
    }, [
      userOptionsResolved,
      session,
    ]);

  const resolveUserName = (
    userId:
      unknown,
  ): string => {
    const rawId =
      String(
        userId ??
        "",
      ).trim();

    if (!rawId) {
      return "-";
    }

    return (
      userNameMap.get(
        rawId.toLowerCase(),
      ) ??
      rawId
    );
  };

  const complaintMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const option of complaintOptionsResolved) {
      if (option.value) {
        map.set(option.value.toLowerCase(), option.label);
      }
    }
    return map;
  }, [complaintOptionsResolved]);
  const resolveComplaintCode = (complaintId: unknown): string => {
    const rawId = String(complaintId ?? "").trim();
    if (!rawId) {
      return "-";
    }
    return complaintMap.get(rawId.toLowerCase()) ?? rawId;
  };
  const rawMessagePayload =
    dataOr<unknown>(
      query.data,
      [],
    );
const rawRows:
    readonly Record<
      string,
      unknown
    >[] =
    Array.isArray(
      rawMessagePayload,
    )
      ? rawMessagePayload.filter(
          (
            row,
          ): row is Record<
            string,
            unknown
          > =>
            typeof row ===
              "object" &&
            row !== null &&
            !Array.isArray(row),
        )
      : [];


  const enrichedRows =
    useMemo(
      () =>
        rawRows.map(
          (row) => {
            const persistedThreadId =
              String(
                row.threadId ??
                row.messageThreadId ??
                row.id ??
                "",
              ).trim();

            const senderUserId =
              row.latestSenderUserId ??
              row.senderUserId ??
              row.createdByUserId ??
              row.authorId ??
              row.createdBy ??
              row.lastMessageByUserId;

            const senderNameResolved =
              row.latestSenderName ??
              row.senderName ??
              row.createdByName ??
              resolveUserName(
                senderUserId,
              );

            const messageBodyResolved =
              String(
                row.latestMessage ??
                row.messageBody ??
                row.body ??
                row.content ??
                row.lastMessage ??
                row.message ??
                row.description ??
                "",
              );
              const rawComplaintId = String(
              row.complaintId ??
              row.linkedEntityId ??
              row.parentComplaintId ??
              row.sourceEntityId ??
              "",
            ).trim();
            const explicitComplaintCode = String(
              row.complaintNumber ??
              row.complaintCode ??
              row.linkedRecordCode ??
              row.linkedRecord ??
              row.linkedEntityCode ??
              "",
            ).trim();
            const isUuid = (val: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);

            const linkedRecordResolved =
              explicitComplaintCode && explicitComplaintCode !== "-" && explicitComplaintCode.toLowerCase() !== "general" && !isUuid(explicitComplaintCode)
                ? explicitComplaintCode
                : (rawComplaintId && complaintMap.has(rawComplaintId.toLowerCase())
                    ? complaintMap.get(rawComplaintId.toLowerCase())!
                    : (explicitComplaintCode || rawComplaintId || "-"));


            const recipientIds =
              Array.isArray(
                row.recipientUserIds,
              )
                ? row.recipientUserIds
                    .map((value) =>
                      String(
                        value,
                      ).trim(),
                    )
                    .filter(Boolean)
                : row.recipientUserId
                  ? [
                      String(
                        row.recipientUserId,
                      ).trim(),
                    ]
                  : [];

            const recipientNames =
              Array.isArray(
                row.recipientNames,
              ) &&
              row.recipientNames
                .length > 0
                ? row.recipientNames
                    .map(String)
                : recipientIds.map(
                    (
                      recipientId,
                    ) =>
                      resolveUserName(
                        recipientId,
                      ),
                  );

            return {
              ...row,

              id:
                persistedThreadId,

              threadId:
                persistedThreadId,

              senderUserId:
                senderUserId,

              senderName:
                String(
                  senderNameResolved ??
                  "-",
                ),

              latestSenderName:
                String(
                  senderNameResolved ??
                  "-",
                ),

              body:
                messageBodyResolved,

              latestMessage:
                messageBodyResolved,

              messageBody:
                messageBodyResolved,

              linkedRecordCode:
                linkedRecordResolved,

              linkedRecord:
                linkedRecordResolved,

              /*
               * Original GUID values preserved.
               */
              complaintId:
                row.complaintId,

              linkedEntityId:
                row.linkedEntityId ??
                row.complaintId,

              recipientUserIds:
                recipientIds,

              recipientNames,

              visibility:
                row.visibilityCode ??
                row.visibility ??
                row.confidentiality ??
                "INTERNAL_ONLY",

              visibilityCode:
                row.visibilityCode ??
                row.visibility ??
                row.confidentiality ??
                "INTERNAL_ONLY",

              status:
                row.status ??
                (row.isArchived
                  ? "ARCHIVED"
                  : row.isRead
                    ? "READ"
                    : "UNREAD"),
            };
          },
        ),
      [
        rawRows,
        userNameMap,
      ],
    );

  return (
    <CmtsMessagesPage
      rows={
        enrichedRows
      }
      masterOptions={{
        visibility:
          visibilityOptionsResolved,

        userOptions:
          userOptionsResolved,

        complaintOptions:
          complaintOptionsResolved,
      }}
    diagnostics={diagnosticsOf(
  query.data,
  create.data,
  reply.data,
  archive.data,
  markRead.data,
  markUnread.data,
  createTask.data,
)}
      busy={
        pending ||
        query.isLoading ||
        usersQuery.isLoading ||
        complaintsQuery.isLoading
      }
      error={
        error
      }
      onCreate={async (
        payload,
      ) => {
        await create.mutateAsync(
          payload,
        );
      }}
      onReply={async (
        row,
        payload,
      ) => {
        await reply.mutateAsync({
          row,
          payload,
        });
      }}
      onArchive={async (
        row,
      ) => {
        await archive.mutateAsync(
          row,
        );
      }}
      onMarkRead={async (
        row,
      ) => {
        await markRead.mutateAsync(
          row,
        );
      }}
      onMarkUnread={async (
        row,
      ) => {
        await markUnread.mutateAsync(
          row,
        );
      }}
      onCreateTask={async (
        row,
        payload,
      ) => {
        await createTask.mutateAsync({
          row,
          payload,
        });
      }}
      onOpen={(
        row,
      ) => {
        const path =
          optionalLinkedComplaintPath(
            row,
            "/messages",
          );

        if (path) {
          navigate(path);
        }
      }}
    />
  );
}
export function CmtsMyWorkspaceContainer({
  runtime,
}: CmtsConnectedContainerProps) {
  const navigate =
    useNavigate();

  const queryClient =
    useQueryClient();

  const t =
    tenantId(runtime);

  const session =
    readCmtsSession();

  const actorId =
    session?.userId ??
    "anonymous";

  const tasks =
    useQuery({
      queryKey: [
        ...cmtsKeys.tasks(t),
        actorId,
        "my-work",
      ],

      queryFn: () =>
        runtime.repositories
          .tasks.my(t),

      enabled: Boolean(
        t &&
        session?.userId,
      ),

      staleTime: 30_000,
      retry: 1,
    });

  const notifications =
    useQuery({
      queryKey: [
        ...cmtsKeys.notifications(t),
        actorId,
        "my-work",
      ],

      queryFn: () =>
        runtime.repositories
          .notifications.my(t),

      enabled: Boolean(
        t &&
        session?.userId,
      ),

      staleTime: 30_000,
      retry: 1,
    });

  const messages =
    useQuery({
      queryKey: [
        "cmts",
        "messages",
        t,
        actorId,
        "my-work",
      ],

      queryFn: () =>
        runtime.repositories
          .messages.threads(t),

      enabled: Boolean(
        t &&
        session?.userId,
      ),

      staleTime: 30_000,
      retry: 1,
    });

  /*
   * Complaint lookup.
   *
   * Linked complaint GUID-ஐ complaint number/title-ஆக
   * My Workspace page-ல் காட்ட பயன்படுத்தப்படுகிறது.
   */
  const complaints =
    useComplaintRegister(
      runtime.repositories
        .complaints,

      {
        tenantId: t,
        pageNumber: 1,
        pageSize: 500,
        sortBy:
          "complaintNumber",
        sortDirection:
          "DESC",
      },
    );

  const markRead =
    useMutation({
      mutationFn: (
        id: string,
      ) =>
        runtime.repositories
          .notifications
          .markRead(
            requireCmtsGuid(
              id,
              "Notification mark read id",
            ),
            t,
          ),

      onSuccess: async () => {
        await queryClient
          .invalidateQueries({
            queryKey:
              cmtsKeys.notifications(
                t,
              ),
          });
      },
    });

  const markMessageRead =
    useMutation({
      mutationFn: (
        id: string,
      ) =>
        runtime.repositories
          .messages
          .markRead(
            requireCmtsGuid(
              id,
              "Message mark read id",
            ),
            t,
          ),

      onSuccess: async () => {
        await queryClient
          .invalidateQueries({
            queryKey: [
              "cmts",
              "messages",
              t,
            ],
          });
      },
    });

  const complete =
    useMutation({
      mutationFn: (
        task:
          WorkspaceRow,
      ) =>
        runtime.repositories
          .tasks.complete(
            workspaceId(
              task,
              [
                "id",
                "taskId",
              ],
              "Workspace task complete",
            ),

            {
              completedBy:
                session?.userId,

              remarks:
                `Completed ${String(
                  task.title ??
                  task.id ??
                  "task",
                )} from My Work.`,
            },

            t,
          ),

      onSuccess: async () => {
        await queryClient
          .invalidateQueries({
            queryKey:
              cmtsKeys.tasks(t),
          });
      },
    });

  const refresh =
    async () => {
      await Promise.all([
        queryClient
          .invalidateQueries({
            queryKey:
              cmtsKeys.tasks(t),
          }),

        queryClient
          .invalidateQueries({
            queryKey:
              cmtsKeys.notifications(
                t,
              ),
          }),

        queryClient
          .invalidateQueries({
            queryKey: [
              "cmts",
              "messages",
              t,
            ],
          }),

        queryClient
          .invalidateQueries({
            queryKey:
              cmtsKeys.complaints(
                t,
              ),
          }),
      ]);
    };

  const openLinked = (
    row:
      WorkspaceRow,
  ) => {
    const path =
      optionalLinkedComplaintPath(
        row,
        "/workflow",
      );

    if (path) {
      navigate(path);
      return;
    }

    if (
      String(
        row.workType ??
        "",
      ).toLowerCase() ===
        "message"
    ) {
      navigate(
        "/cmts/messages",
      );
      return;
    }

    navigate(
      "/cmts/my-work/tasks",
    );
  };

  const complaintRows =
    complaints.data?.ok
      ? complaints.data.data
      : [];

  const complaintOptions =
    complaintRows
      .map((row) => {
        const record =
          row as unknown as
            Record<
              string,
              unknown
            >;

        const value =
          String(
            record.id ??
            record.complaintId ??
            "",
          ).trim();

        const code =
          String(
            record.complaintNumber ??
            record.complaintCode ??
            record.code ??
            record.registrationNumber ??
            "",
          ).trim();

        const title =
          String(
            record.title ??
            record.complaintTitle ??
            record.subject ??
            "",
          ).trim();

        return {
          value,

          label:
            code && title
              ? `${code} — ${title}`
              : code ||
                title ||
                value,
        };
      })
      .filter(
        (option) =>
          Boolean(
            option.value,
          ),
      );

  return (
    <CmtsMyWorkspacePage
      tasks={
        tasks.data?.ok
          ? tasks.data.data
          : []
      }

      notifications={
        notifications.data?.ok
          ? notifications
              .data.data
          : []
      }

      messages={
        messages.data?.ok
          ? (
              messages.data.data as
                readonly Record<
                  string,
                  unknown
                >[]
            )
          : []
      }

      userId={
        session?.userId
      }

      complaintOptions={
        complaintOptions
      }

      diagnostics={
        runtime.diagnostics
      }

      busy={
        tasks.isLoading ||
        notifications.isLoading ||
        messages.isLoading ||
        complaints.isLoading ||
        complete.isPending ||
        markRead.isPending ||
        markMessageRead.isPending
      }

      onOpenTask={(
        task:
          EnterpriseTask,
      ) =>
        openLinked(
          task as WorkspaceRow,
        )
      }

      onCompleteTask={async (
        task:
          EnterpriseTask,
      ) => {
        await complete
          .mutateAsync(
            task as WorkspaceRow,
          );
      }}

      onMarkRead={async (
        notification:
          EnterpriseNotification,
      ) => {
        await markRead
          .mutateAsync(
            workspaceId(
              notification as WorkspaceRow,
              [
                "id",
                "notificationId",
              ],
              "Notification mark read",
            ),
          );
      }}

      onOpenNotification={async (
        notification:
          EnterpriseNotification,
      ) => {
        openLinked(
          notification as WorkspaceRow,
        );
      }}

      onOpenMessage={async (
        message,
      ) => {
        const threadId =
          String(
            message.threadId ??
            message.messageThreadId ??
            "",
          ).trim();

        if (threadId) {
          navigate(
            `/cmts/messages?threadId=${encodeURIComponent(
              threadId,
            )}`,
          );
          return;
        }

        openLinked(
          message,
        );
      }}

      onMarkMessageRead={async (
        message,
      ) => {
        await markMessageRead
          .mutateAsync(
            workspaceId(
              message,
              [
                "threadId",
                "messageThreadId",
                "id",
              ],
              "Message mark read",
            ),
          );
      }}

      onOpenAuditTrail={(
        row,
      ) => {
        const path =
          optionalLinkedComplaintPath(
            row,
            "/history",
          );

        navigate(
          path ??
          "/cmts/audit/logs",
        );
      }}

      onOpenOperationSpine={(
        row,
      ) => {
        const path =
          optionalLinkedComplaintPath(
            row,
            "/workflow",
          );

        navigate(
          path ??
          "/cmts/operations/system-health",
        );
      }}

      onRefresh={
        refresh
      }
    />
  );
}

export function CmtsNotificationsContainer({
  runtime,
}: CmtsConnectedContainerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const t = tenantId(runtime);

  const session = readCmtsSession();
  const actorId = session?.userId ?? "anonymous";

  const notificationsQueryKey = [...cmtsKeys.notifications(t), actorId];
  const tasksQueryKey = cmtsKeys.tasks(t);

  const query = useQuery({
    queryKey: notificationsQueryKey,
    queryFn: () => runtime.repositories.notifications.my(t),
    enabled: Boolean(t && session?.userId),
    staleTime: 15_000,
    retry: 1,
  });

  const complaints = useComplaintRegister(runtime.repositories.complaints, {
    tenantId: t,
    pageNumber: 1,
    pageSize: 500,
    sortBy: "complaintNumber",
    sortDirection: "DESC",
  });

  const complaintOptions = (complaints.data?.ok ? complaints.data.data : [])
    .map((row) => {
      const record = row as unknown as Record<string, unknown>;
      const value = String(record.id ?? record.complaintId ?? "").trim();
      const code = String(record.complaintNumber ?? record.complaintCode ?? record.code ?? record.registrationNumber ?? "").trim();
      return { value, label: code || value };
    })
    .filter((option) => Boolean(option.value));

  /*
   * Notification status action முடிந்தவுடன் active notification
   * query கட்டாயமாக refetch ஆகும்.
   *
   * இதனால் READ / UNREAD / ACKNOWLEDGED / ARCHIVED status
   * backend response பிறகு grid-ல் உடனே மாறும்.
   */
  const refreshNotifications = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: notificationsQueryKey,
      refetchType: "active",
    });

    await queryClient.refetchQueries({
      queryKey: notificationsQueryKey,
      type: "active",
    });
  };

  /*
   * Create Task action notification list மற்றும் task list
   * இரண்டையும் refresh செய்யும்.
   */
  const refreshNotificationsAndTasks = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: notificationsQueryKey,
        refetchType: "active",
      }),
      queryClient.invalidateQueries({
        queryKey: tasksQueryKey,
        refetchType: "active",
      }),
    ]);

    await Promise.all([
      queryClient.refetchQueries({
        queryKey: notificationsQueryKey,
        type: "active",
      }),
      queryClient.refetchQueries({
        queryKey: tasksQueryKey,
        type: "active",
      }),
    ]);
  };

  const markRead = useMutation({
    mutationFn: (id: string) =>
      runtime.repositories.notifications.markRead(
        requireCmtsGuid(id, "Notification mark read id"),
        t,
      ),

    onSuccess: async () => {
      await refreshNotifications();
    },
  });

  const markUnread = useMutation({
    mutationFn: (id: string) =>
      runtime.repositories.notifications.markUnread(
        requireCmtsGuid(id, "Notification mark unread id"),
        t,
      ),

    onSuccess: async () => {
      await refreshNotifications();
    },
  });

  const acknowledge = useMutation({
    mutationFn: (row: WorkspaceRow) =>
      runtime.repositories.notifications.acknowledge(
        workspaceId(row, ["id", "notificationId"], "Notification acknowledge"),
        {
          acknowledgedBy: readCmtsSession()?.userId,
        },
        t,
      ),

    onSuccess: async () => {
      await refreshNotifications();
    },
  });

  const archive = useMutation({
    mutationFn: (row: WorkspaceRow) =>
      runtime.repositories.notifications.archive(
        workspaceId(row, ["id", "notificationId"], "Notification archive"),
        {
          archivedBy: readCmtsSession()?.userId,
        },
        t,
      ),

    onSuccess: async () => {
      await refreshNotifications();
    },
  });

  const execute = useMutation({
    mutationFn: (row: WorkspaceRow) =>
      runtime.repositories.notifications.executeAction(
        workspaceId(row, ["id", "notificationId"], "Notification execute"),
        {
          actionCode: row.actionCode ?? "OPEN_LINKED_RECORD",
        },
        t,
      ),

    onSuccess: async () => {
      await refreshNotifications();
    },
  });

  const createTask = useMutation({
    mutationFn: ({
      row,
      payload,
    }: {
      row: WorkspaceRow;
      payload: Record<string, unknown>;
    }) =>
      runtime.repositories.notifications.createTask(
        workspaceId(row, ["id", "notificationId"], "Notification create task"),
        payload,
        t,
      ),

    onSuccess: async () => {
      await refreshNotificationsAndTasks();
    },
  });

  const pending =
    markRead.isPending ||
    markUnread.isPending ||
    acknowledge.isPending ||
    archive.isPending ||
    execute.isPending ||
    createTask.isPending;

  const error = firstUserSafeError([
    markRead.error,
    markUnread.error,
    acknowledge.error,
    archive.error,
    execute.error,
    createTask.error,
  ]);

  /*
   * Notification EventType அடிப்படையில் complaint-ன்
   * சரியான module route open செய்யப்படும்.
   *
   * RCA.*          -> /complaints/{id}/rca
   * CAPA.*         -> /complaints/{id}/capa
   * VERIFICATION.* -> /complaints/{id}/verification
   * மற்றவை         -> /complaints/{id}/workflow
   */
  const openLinked = (row: WorkspaceRow): void => {
    const eventType = String(
      row.eventType ?? row.sourceModule ?? row.linkedEntityType ?? "",
    )
      .trim()
      .toUpperCase();

    let linkedSection = "/workflow";

    if (eventType.includes("VERIFICATION")) {
      linkedSection = "/verification";
    } else if (eventType.includes("CAPA")) {
      linkedSection = "/capa";
    } else if (eventType.includes("RCA")) {
      linkedSection = "/rca";
    }

    const path = optionalLinkedComplaintPath(row, linkedSection);

    if (path) {
      navigate(path);
      return;
    }

    navigate("/cmts/notifications");
  };

  return (
    <CmtsNotificationsPage
      notifications={dataOr(query.data, [])}
      complaintOptions={complaintOptions}
      diagnostics={runtime.diagnostics}
      busy={query.isLoading || query.isFetching || complaints.isLoading || pending}
      error={error}
      onMarkRead={async (notification: EnterpriseNotification) => {
        const row = notification as WorkspaceRow;

        const id = workspaceId(
          row,
          ["id", "notificationId"],
          "Notification mark read",
        );

        await markRead.mutateAsync(id);
      }}
      onMarkUnread={async (notification: EnterpriseNotification) => {
        const row = notification as WorkspaceRow;

        const id = workspaceId(
          row,
          ["id", "notificationId"],
          "Notification mark unread",
        );

        await markUnread.mutateAsync(id);
      }}
      onAcknowledge={async (notification: EnterpriseNotification) => {
        await acknowledge.mutateAsync(notification as WorkspaceRow);
      }}
      onArchive={async (notification: EnterpriseNotification) => {
        await archive.mutateAsync(notification as WorkspaceRow);
      }}
      onExecuteAction={async (notification: EnterpriseNotification) => {
        await execute.mutateAsync(notification as WorkspaceRow);
      }}
      onCreateTask={async (
        notification: EnterpriseNotification,
        payload: Record<string, unknown>,
      ) => {
        await createTask.mutateAsync({
          row: notification as WorkspaceRow,
          payload,
        });
      }}
      onOpenLinkedRecord={async (notification: EnterpriseNotification) => {
        openLinked(notification as WorkspaceRow);
      }}
    />
  );
}
export function CmtsTasksContainer({ runtime }: CmtsConnectedContainerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const t = tenantId(runtime);
  const storedSession = readCmtsSession();
  const effectivePermissions = new Set(
    [
      ...(storedSession?.permissions ?? []),
      ...(storedSession?.permissionCodes ?? []),
    ].map(String),
  );
  const canAssignTasks = effectivePermissions.has("TASK.ASSIGN");
  const currentUserOptions = storedSession?.userId
    ? [
        {
          value: storedSession.userId,
          label:
            storedSession.displayName || storedSession.email || "Current user",
        },
      ]
    : [];
  const query = useQuery({
    queryKey: cmtsKeys.tasks(t),
    queryFn: () => runtime.repositories.tasks.my(t),
    staleTime: 30_000,
    retry: 1,
  });
  const priorityOptions = useQuery({
    queryKey: ["cmts", "master-options", "task-priority", t],
    queryFn: () => loadMasterOptions(runtime, "priority", t, "priority"),
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const userOptions = useQuery({
    queryKey: ["cmts", "task-users", t],
    queryFn: () => runtime.repositories.reference.users(t),
    staleTime: 60_000,
    retry: 1,
    enabled: canAssignTasks,
  });
  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: cmtsKeys.tasks(t) });
    await queryClient.invalidateQueries({ queryKey: ["cmts", "messages", t] });
    await queryClient.invalidateQueries({
      queryKey: cmtsKeys.notifications(t),
    });
  };
  const create = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      runtime.repositories.tasks.create(
        {
          ...payload,
          status: payload.status ?? "open",
          ownerId: payload.ownerId || readCmtsSession()?.userId,
        },
        t,
      ),
    onSuccess: invalidate,
  });
  const complete = useMutation({
    mutationFn: ({
      task,
      payload,
    }: {
      task: WorkspaceRow;
      payload?: Record<string, unknown> | undefined;
    }) =>
      runtime.repositories.tasks.complete(
        workspaceId(task, ["id", "taskId"], "Task complete"),
        {
          completedBy: readCmtsSession()?.userId,
          remarks: payload?.remarks ?? "Completed from task grid",
        },
        t,
      ),
    onSuccess: invalidate,
  });
  const acknowledge = useMutation({
    mutationFn: (task: WorkspaceRow) =>
      runtime.repositories.tasks.acknowledge(
        workspaceId(task, ["id", "taskId"], "Task acknowledge"),
        { acknowledgedBy: readCmtsSession()?.userId },
        t,
      ),
    onSuccess: invalidate,
  });
  const reassign = useMutation({
    mutationFn: ({
      task,
      payload,
    }: {
      task: WorkspaceRow;
      payload: Record<string, unknown>;
    }) =>
      runtime.repositories.tasks.assign(
        workspaceId(task, ["id", "taskId"], "Task reassign"),
        payload,
        t,
      ),
    onSuccess: invalidate,
  });
  const comment = useMutation({
    mutationFn: ({
      task,
      payload,
    }: {
      task: WorkspaceRow;
      payload: Record<string, unknown>;
    }) =>
      runtime.repositories.tasks.comment(
        workspaceId(task, ["id", "taskId"], "Task comment"),
        payload,
        t,
      ),
    onSuccess: invalidate,
  });
  const snooze = useMutation({
    mutationFn: ({
      task,
      payload,
    }: {
      task: WorkspaceRow;
      payload: Record<string, unknown>;
    }) =>
      runtime.repositories.tasks.snooze(
        workspaceId(task, ["id", "taskId"], "Task snooze"),
        payload,
        t,
      ),
    onSuccess: invalidate,
  });
  const pending =
    create.isPending ||
    complete.isPending ||
    acknowledge.isPending ||
    reassign.isPending ||
    comment.isPending ||
    snooze.isPending;
  const error = firstUserSafeError([
    create.error,
    complete.error,
    acknowledge.error,
    reassign.error,
    comment.error,
    snooze.error,
  ]);
  const openLinked = (task: EnterpriseTask) => {
    const path = optionalLinkedComplaintPath(task as WorkspaceRow, "/workflow");
    if (path) navigate(path);
    else navigate("/cmts/my-work/tasks");
  };
  const usersPayload = canAssignTasks ? dataOr(userOptions.data, []) : [];

  const users: readonly Record<string, unknown>[] = Array.isArray(usersPayload)
    ? (usersPayload as readonly Record<string, unknown>[])
    : usersPayload &&
        typeof usersPayload === "object" &&
        Array.isArray((usersPayload as Record<string, unknown>).items)
      ? (
          usersPayload as {
            items: readonly Record<string, unknown>[];
          }
        ).items
      : usersPayload &&
          typeof usersPayload === "object" &&
          Array.isArray((usersPayload as Record<string, unknown>).records)
        ? (
            usersPayload as {
              records: readonly Record<string, unknown>[];
            }
          ).records
        : [];
  const taskUserOptions = canAssignTasks
    ? users
        .map((row) => {
          const userId = String(row.value ?? row.userId ?? row.id ?? "").trim();
          const code = String(row.code ?? row.userCode ?? "").trim();
          const metadataEmail =
            row.metadata && typeof row.metadata === "object"
              ? String((row.metadata as Record<string, unknown>).email ?? "").trim()
              : "";

          const rawLabel = String(
            row.label ??
            row.fullName ??
            row.displayName ??
            row.userName ??
            row.name ??
            row.loginId ??
            row.email ??
            metadataEmail ??
            "",
          ).trim();

          const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(rawLabel);
          const baseName = !isGuid ? rawLabel : (code || rawLabel);

          const userName =
            baseName && code && code.toLowerCase() !== baseName.toLowerCase()
              ? `${baseName} (${code})`
              : baseName || code || userId;

          return {
            value: userId,
            label: userName,
          };
        })
        .filter((option) => Boolean(option.value))
    : currentUserOptions;
  const complaintsQuery = useQuery({
    queryKey: ["cmts", "task-complaints", t],
    queryFn: () =>
      runtime.repositories.complaints.list({
        tenantId: t,
        pageNumber: 1,
        pageSize: 200,
      }),
    enabled: Boolean(t),
    retry: 1,
  });
  const complaintsPayload = dataOr<unknown>(complaintsQuery.data, []);
  const complaintRows: readonly Record<string, unknown>[] = Array.isArray(complaintsPayload)
    ? (complaintsPayload as readonly Record<string, unknown>[])
    : complaintsPayload && typeof complaintsPayload === "object"
      ? (Array.isArray((complaintsPayload as Record<string, unknown>).items)
          ? (complaintsPayload as { items: readonly Record<string, unknown>[] }).items
          : Array.isArray((complaintsPayload as Record<string, unknown>).records)
            ? (complaintsPayload as { records: readonly Record<string, unknown>[] }).records
            : Array.isArray((complaintsPayload as Record<string, unknown>).complaints)
              ? (complaintsPayload as { complaints: readonly Record<string, unknown>[] }).complaints
              : [])
      : [];

  const taskComplaintOptions = useMemo(
    () =>
      complaintRows
        .map((row) => ({
          value: String(row.id ?? row.complaintId ?? "").trim(),
          label: String(
            row.complaintNumber ??
              row.complaintCode ??
              row.code ??
              row.title ??
              "Complaint",
          ).trim(),
        }))
        .filter((option) => Boolean(option.value)),
    [complaintRows],
  );

  const rolesQuery = useQuery({
    queryKey: ["cmts", "task-roles", t],
    queryFn: () => runtime.repositories.reference.roles(t),
    enabled: Boolean(t && canAssignTasks),
    retry: false,
  });
  const rolesPayload = dataOr<unknown>(rolesQuery.data, []);
  const rolesArr: readonly Record<string, unknown>[] = Array.isArray(rolesPayload)
    ? (rolesPayload as readonly Record<string, unknown>[])
    : rolesPayload && typeof rolesPayload === "object"
      ? (Array.isArray((rolesPayload as Record<string, unknown>).items)
          ? (rolesPayload as { items: readonly Record<string, unknown>[] }).items
          : Array.isArray((rolesPayload as Record<string, unknown>).records)
            ? (rolesPayload as { records: readonly Record<string, unknown>[] }).records
            : Array.isArray((rolesPayload as Record<string, unknown>).roles)
              ? (rolesPayload as { roles: readonly Record<string, unknown>[] }).roles
              : [])
      : [];

  const taskRoleOptions = useMemo(
    () =>
      rolesArr
        .map((row) => ({
          value: String(row.code ?? row.roleCode ?? row.name ?? row.id ?? "").trim(),
          label: String(row.name ?? row.displayName ?? row.roleName ?? row.code ?? "").trim(),
        }))
        .filter((option) => Boolean(option.value)),
    [rolesArr],
  );

  return (
    <CmtsTasksPage
      tasks={dataOr(query.data, [])}
      masterOptions={{
        priority: priorityOptions.data ?? [],
        users: taskUserOptions,
        complaints: taskComplaintOptions,
        roles: taskRoleOptions,
      }}
      diagnostics={diagnosticsOf(
        query.data,
        create.data,
        complete.data,
        acknowledge.data,
        reassign.data,
        comment.data,
        snooze.data,
      )}
      busy={pending}
      error={error}
      onCreateTask={async (payload) => {
        await create.mutateAsync(payload);
      }}
      onOpenTask={openLinked}
      onCompleteTask={async (
        task: EnterpriseTask,
        payload?: Record<string, unknown>,
      ) => {
        await complete.mutateAsync({ task: task as WorkspaceRow, payload });
      }}
      onAcknowledgeTask={async (task: EnterpriseTask) => {
        await acknowledge.mutateAsync(task as WorkspaceRow);
      }}
      onReassignTask={async (task: EnterpriseTask, payload) => {
        await reassign.mutateAsync({ task: task as WorkspaceRow, payload });
      }}
      onCommentTask={async (task: EnterpriseTask, payload) => {
        await comment.mutateAsync({ task: task as WorkspaceRow, payload });
      }}
      onSnoozeTask={async (task: EnterpriseTask, payload) => {
        await snooze.mutateAsync({ task: task as WorkspaceRow, payload });
      }}
      onOpenAuditTrail={(task: EnterpriseTask) => {
        const path = optionalLinkedComplaintPath(
          task as WorkspaceRow,
          "/history",
        );
        navigate(path ?? "/cmts/audit/logs");
      }}
      onOpenOperationSpine={(task: EnterpriseTask) => {
        const path = optionalLinkedComplaintPath(
          task as WorkspaceRow,
          "/workflow",
        );
        navigate(path ?? "/cmts/operations/system-health");
      }}
    />
  );
}

